<template>
  <div>
    <base-breadcrumb-bar :pageTitle="'Manage All Users'" :tabItems="tabs" :tab.sync="tab">
      <!-- Single slot, switch by tab -->
      <template v-slot:filterBlock>
        <!-- Users tab filters -->
        <div v-if="tab === 0">
          <v-row class="mb-1">
            <v-col cols="5">
              <v-text-field
                v-model="search"
                class="ml-4"
                clearable
                placeholder="Search"
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="5">
              <role-picker
                outlined
                :chosen-role.sync="roleSelection"
                :initial="roleSelectionDefault"
              />
            </v-col>
            <v-col cols="2" class="legal-checkbox full-widthed">
              <div class="small-switch-box">
                <div class="title-side"><p>Archived Users</p></div>
                <div class="switch-side">
                  <v-switch v-model="isArchived" hide-details color="primary" inset />
                  <span style="opacity:.5">{{ !isArchived ? "Excld" : "Incld" }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Analytics tab filters (broker + date range + buttons) -->
        <div v-else>
          <v-row class="mb-1 mt-4" align="center" no-gutters>
            <v-col cols="12" md="4" class="pr-md-2 mb-2 mb-md-0">
              <base-user-picker
                class="user-picker-field"
                label="Select Broker"
                placeholder="Select Broker"
                solo
                background-color="#E8E8E8"
                clearable
                :isMultiple="false"
                :chosen-user.sync="analyticsUser"
                :initial="analyticsUser"
                :showNull="true"
                :isDisabled="me && me.role === 'broker'"
                :limitBy="(me && (me.role === 'admin' || me.role === 'manager') && me.division) ? { division: me.division } : null"
              />
            </v-col>

    <v-col cols="12" md="5" class="d-flex align-center">
  <v-text-field
    v-model="analyticsFrom"
    type="date"
    solo
    hide-details
    background-color="#E8E8E8"
    prepend-inner-icon="mdi-calendar"
    class="picker-like mr-2"
  />
  <span class="mx-1">to</span>
  <v-text-field
    v-model="analyticsTo"
    type="date"
    solo
    hide-details
    background-color="#E8E8E8"
    prepend-inner-icon="mdi-calendar"
    class="picker-like ml-2"
  />
</v-col>


    <v-col cols="12" md="3" class="d-flex  mt-2 mt-md-0">
  <v-btn
    depressed
    class="picker-btn picker-btn--apply ml-2 mr-2"
    @click="applyAnalyticsFilters"
  >
    <v-icon left>mdi-magnify</v-icon>Apply
  </v-btn>

  <v-btn
    depressed
    class="picker-btn picker-btn--clear"
    @click="clearAnalyticsFilters"
  >
    <v-icon left>mdi-close</v-icon>Clear
  </v-btn>
</v-col>

          </v-row>
        </div>
      </template>
    </base-breadcrumb-bar>

    <!-- Users tab -->
    <admin-section
      v-if="tab === 0"
      :isPage="true"
      :passedRole="roleSelection"
      :passedIsArchived="isArchived"
      :roleSelection.sync="roleSelection"
      :passedSearch="search"
    />

    <!-- Analytics tab -->
    <user-analytics
      v-if="tab === 1"
      ref="analytics"
      :me="me"
    />
  </div>
</template>

<script>
import { gql } from "apollo-boost";
import RolePicker from "@/components/Base/Pickers/RolePicker.vue";
import AdminSection from "@/components/Base/User/AdminSection.vue";
import UserAnalytics from "@/components/Base/User/UserAnalytics.vue";

/* Helpers: last 30 days */
const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const lastNDaysRange = (n = 30) => {
  const today = new Date();
  const to = toInputDate(today);
  const start = new Date(today);
  start.setDate(today.getDate() - (n - 1)); // inclusive
  return { from: toInputDate(start), to };
};

export default {
  name: "ViewAdmins",
  components: { RolePicker, AdminSection, UserAnalytics },
  data() {
    const { from, to } = lastNDaysRange(30);
    return {
      // Users tab state
      search: "",
      isArchived: false,
      roleSelection: ["user", "admin", "broker", "superAdmin", "manager"],
      roleSelectionDefault: ["user", "admin", "broker", "superAdmin", "manager"],

      // Tabs
      tabs: [
        { text: "Users", icon: "mdi-account-multiple" },
        { text: "Analytics", icon: "mdi-chart-bar-stacked" },
      ],
      tab: 0,

      // Analytics filters (lives in parent breadcrumb)
      analyticsUser: null,
      analyticsFrom: from,
      analyticsTo: to,
    };
  },

  apollo: {
    me: gql`
      query {
        me {
          id
          email
          firstName
          lastName
          role
          division
          team
        }
      }
    `,
  },

  methods: {
    applyAnalyticsFilters() {
      const c = this.$refs.analytics;
      if (!c) return;
      // Push the breadcrumb values down to the analytics component and trigger its fetch
      c.userDraft = this.analyticsUser;
      c.dateFromDraft = this.analyticsFrom;
      c.dateToDraft = this.analyticsTo;
      if (typeof c.applyFilters === "function") c.applyFilters();
    },
    clearAnalyticsFilters() {
      const c = this.$refs.analytics;
      const { from, to } = lastNDaysRange(30);
      this.analyticsUser = null;
      this.analyticsFrom = from;
      this.analyticsTo = to;
      if (c) {
        c.userDraft = null;
        c.dateFromDraft = from;
        c.dateToDraft = to;
        if (typeof c.clearFilters === "function") c.clearFilters();
      }
    },
  },
};
</script>

<style scoped>

/* Base: same visual rhythm as your picker/date fields */
.picker-btn{
  height:54px !important;                 /* match input height */
  border-radius:8px;          /* same rounded corners */
  padding:0 14px;
  text-transform:none;
  font-weight:600;
  letter-spacing:.2px;
  box-shadow:none !important;
}

.picker-btn .v-icon{
  font-size:18px;
  margin-right:6px;
}

/* Apply = primary filled */
.picker-btn--apply{
  background:#F37168 !important;
  color:#fff !important;
  border:1px solid #F37168;
}
.picker-btn--apply .v-icon{ color:#fff !important; }

/* Clear = neutral pill, like inputs */
.picker-btn--clear{
  background:#E8E8E8 !important;
  color:#374151 !important;
  border:1px solid #D9DCE3;
}
.picker-btn--clear .v-icon{ color:#374151 !important; }

/* Consistent focus ring */
.picker-btn:focus{
  box-shadow:0 0 0 2px rgba(156,163,175,.35) !important;
}

/* Vue 2 + Vuetify 2 */
.picker-like ::v-deep .v-input__slot,
/* Vue 3 */
.picker-like :deep(.v-input__slot) {
  background:#fff !important;
  border: 0px solid #D9DCE3;
  border-radius:12px;
  min-height:56px;
  padding:0 10px;
  box-shadow:none !important;
}

.picker-like ::v-deep input,
.picker-like :deep(input) {
  font-size:14px;
  letter-spacing:.2px;
}

.picker-like ::v-deep .v-input__prepend-inner .v-icon,
.picker-like :deep(.v-input__prepend-inner .v-icon) {
  opacity:.65;
}

.picker-like ::v-deep .v-input__slot:focus-within,
.picker-like :deep(.v-input__slot:focus-within) {
  border-color:#9CA3AF;
  box-shadow:0 0 0 2px rgba(156,163,175,.35);
}


.user-picker-field { min-width: 16rem; }
.date-input{
  border:1px solid #d1d5db;
  border-radius:0.375rem;
  padding:0.5rem 0.75rem;
  background:#fff;
}
.date-input:focus{
  outline:none;
  border-color:#9ca3af;
  box-shadow:0 0 0 2px rgba(156,163,175,.35);
}
</style>
